import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { ChevronLeft, GripVertical, Pin, Plus, Search, StickyNote, X } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { NoteEditorModal } from '@/components/NoteEditorModal';
import { LinkPreviewCard } from '@/components/LinkPreviewCard';
import { RichTextView } from '@/components/RichTextView';
import { extractSingleUrl } from '@/lib/link-preview';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { goBackOrTo } from '@/lib/nav';
import { bumpDataRevision } from '@/lib/data-revision';
import {
  addNote,
  deleteNote,
  listNotes,
  noteDisplayTitle,
  noteSnippet,
  reorderNotes,
  updateNote,
} from '@/lib/notes';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';
import { webTransition } from '@/lib/hover-style';
import type { NoteRow, PropertyRow } from '@/db/types';

/**
 * Notes page — full read/manage surface for the active home's notes.
 *
 *   - Header: page title + search + "+ New" button.
 *   - List: pinned-first, then most-recently-updated. Each row shows
 *     the title and a snippet; tapping opens the editor.
 *   - Empty state: nudge with an example so users understand what
 *     notes are for.
 *
 * Deep linkable: `/notes?focus=<noteId>` auto-opens that note in the
 * editor. Used by the timeline row click handler so tapping a note
 * entry in the timeline lands you straight in editing mode.
 */
export default function NotesPage() {
  const router = useRouter();
  const db = useSQLiteContext();
  const params = useLocalSearchParams<{ focus?: string }>();
  const [property, setProperty] = useState<PropertyRow | null>(null);
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<{ visible: boolean; note: NoteRow | null }>({
    visible: false,
    note: null,
  });

  // First load — fetch property + notes. We don't refresh on every
  // re-mount because the editor save handler already calls listNotes
  // again to update local state.
  useEffect(() => {
    (async () => {
      const prop = await getActiveProperty(db);
      setProperty(prop);
      if (prop) {
        const rows = await listNotes(db, prop.id, 500);
        setNotes(rows);
      }
    })();
  }, [db]);

  // Deep-link: ?focus=<id> opens the matching note ONCE when notes
  // first load. Without the auto-open guard this effect re-ran on
  // every notes change (e.g. after the user saved a new note) and
  // re-opened the focused note's editor — which looked like a phantom
  // "note pops up after I save". Track consumption with a ref so the
  // effect fires exactly once per focus value.
  const autoOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!params.focus || notes.length === 0) return;
    if (autoOpenedRef.current === params.focus) return;
    const target = notes.find((n) => n.id === params.focus);
    if (target) {
      setEditor({ visible: true, note: target });
      autoOpenedRef.current = params.focus;
    }
  }, [params.focus, notes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => {
      const t = noteDisplayTitle(n).toLowerCase();
      const s = noteSnippet(n, 400).toLowerCase();
      return t.includes(q) || s.includes(q);
    });
  }, [notes, query]);

  const refresh = async () => {
    if (!property) return;
    const rows = await listNotes(db, property.id, 500);
    setNotes(rows);
    bumpDataRevision();
  };

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <View style={styles.header}>
        <Pressable
          accessibilityLabel="Back"
          hitSlop={8}
          onPress={() => goBackOrTo(router, '/(tabs)/my-home')}
          style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
          <ChevronLeft size={20} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.h1}>Notes</Text>
          <Text style={styles.sub}>
            {notes.length === 0
              ? 'Anything that doesn’t fit into a task'
              : `${notes.length} note${notes.length === 1 ? '' : 's'} for this home`}
          </Text>
        </View>
        <Pressable
          accessibilityLabel="Add note"
          onPress={() => setEditor({ visible: true, note: null })}
          style={({ pressed, hovered }) => [
            styles.newBtn,
            webTransition,
            hovered && { backgroundColor: Palette.forestLight },
            pressed && { transform: [{ scale: 0.96 }] },
          ]}>
          <Plus size={14} color={Palette.cream} strokeWidth={2.4} />
          <Text style={styles.newBtnText}>New note</Text>
        </Pressable>
      </View>

      {/* Search input — visible whenever there's at least one note. */}
      {notes.length > 0 ? (
        <View style={styles.searchRow}>
          <View style={styles.searchWrap}>
            <Search size={14} color={Palette.inkFaint} strokeWidth={2} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Search notes…"
              placeholderTextColor={Palette.inkFaint}
              style={[styles.searchInput, webNoOutline]}
            />
            {query ? (
              <Pressable onPress={() => setQuery('')} hitSlop={8}>
                <X size={14} color={Palette.inkSoft} strokeWidth={2} />
              </Pressable>
            ) : null}
          </View>
        </View>
      ) : null}

      <ScrollView contentContainerStyle={styles.body}>
        {notes.length === 0 ? (
          <View style={styles.empty}>
            <View style={styles.emptyIcon}>
              <StickyNote size={28} color={Palette.honeyDeep} strokeWidth={1.7} />
            </View>
            <Text style={styles.emptyTitle}>Your first note</Text>
            <Text style={styles.emptyText}>
              Notes capture the stuff that doesn’t fit into a task — paint
              colors, HOA rules, which breaker is for the porch light, the
              septic guy’s favorite coffee. Searchable, pinnable, and
              exported into your Home Record PDF.
            </Text>
            <Pressable
              onPress={() => setEditor({ visible: true, note: null })}
              style={({ pressed }) => [
                styles.emptyCta,
                pressed && { transform: [{ scale: 0.97 }] },
              ]}>
              <Plus size={14} color={Palette.cream} strokeWidth={2.4} />
              <Text style={styles.emptyCtaText}>Write a note</Text>
            </Pressable>
          </View>
        ) : filtered.length === 0 ? (
          <View style={styles.noResults}>
            <Text style={styles.noResultsText}>
              No notes match “{query}”. Try fewer letters.
            </Text>
          </View>
        ) : (
          // Card grid — on desktop the grid wraps to 2 columns at 720px+
          // via flex-wrap + minWidth on each card. On mobile it stacks.
          // Drag-and-drop reorder (web only) lifts a card and drops it
          // into a new slot; the new order persists via reorderNotes.
          // Disabled when a search filter is active so the user doesn't
          // accidentally reorder against a filtered subset.
          <View style={styles.grid}>
            {filtered.map((n, idx) => (
              <NoteCard
                key={n.id}
                note={n}
                onOpen={() => setEditor({ visible: true, note: n })}
                draggable={query.trim().length === 0}
                index={idx}
                onReorder={async (fromIdx, toIdx) => {
                  if (fromIdx === toIdx || !property) return;
                  // Reorder against the live notes array (NOT filtered)
                  // so manual ordering persists across search sessions.
                  const next = notes.slice();
                  const [moved] = next.splice(fromIdx, 1);
                  next.splice(toIdx, 0, moved);
                  setNotes(next);
                  await reorderNotes(db, next.map((row) => row.id));
                }}
              />
            ))}
          </View>
        )}
      </ScrollView>

      <NoteEditorModal
        visible={editor.visible}
        note={editor.note}
        onClose={() => setEditor({ visible: false, note: null })}
        onSave={async (input) => {
          if (!property) return;
          if (editor.note) {
            await updateNote(db, editor.note.id, input);
          } else {
            await addNote(db, {
              propertyId: property.id,
              title: input.title,
              body: input.body,
              pinned: input.pinned,
            });
          }
          await refresh();
        }}
        onDelete={
          editor.note
            ? async () => {
                if (!editor.note || !property) return;
                await deleteNote(db, editor.note.id);
                await refresh();
              }
            : undefined
        }
      />
    </SafeAreaView>
  );
}

function NoteCard({
  note,
  onOpen,
  draggable = false,
  index,
  onReorder,
}: {
  note: NoteRow;
  onOpen: () => void;
  draggable?: boolean;
  index: number;
  onReorder?: (fromIdx: number, toIdx: number) => void | Promise<void>;
}) {
  const updatedAgo = formatRelative(note.updated_at);
  // Drag state. We track whether THIS card is the drag source and
  // whether it's currently the drop target so we can fade / outline
  // them respectively. Both reset on dragend / drop.
  const dragRef = useRef<View | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState(false);

  // Attach HTML5 drag-and-drop handlers on web. On native we skip
  // entirely — RN doesn't ship a touch-based reorder library and
  // adding one is its own wave.
  const onDomRef = (node: View | null) => {
    dragRef.current = node;
    if (Platform.OS !== 'web' || !node || !draggable || !onReorder) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dom = node as unknown as HTMLElement;
    if (!dom?.addEventListener) return;
    // Make the element draggable + wire all five events. We use a
    // string mime type ("application/x-homestead-note") with the
    // index as payload so cross-card identification works without
    // closure capture into stale callbacks.
    dom.setAttribute('draggable', 'true');
    const onDragStart = (e: DragEvent) => {
      e.dataTransfer?.setData('application/x-homestead-note', String(index));
      // Hint at the visual move (not copy).
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
      setDragging(true);
    };
    const onDragEnd = () => {
      setDragging(false);
      setDropTarget(false);
    };
    const onDragOver = (e: DragEvent) => {
      // Required for drop to fire; default behavior would reject it.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      setDropTarget(true);
    };
    const onDragLeave = () => setDropTarget(false);
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDropTarget(false);
      const raw = e.dataTransfer?.getData('application/x-homestead-note');
      if (raw == null) return;
      const fromIdx = Number(raw);
      if (Number.isNaN(fromIdx)) return;
      void onReorder(fromIdx, index);
    };
    dom.addEventListener('dragstart', onDragStart);
    dom.addEventListener('dragend', onDragEnd);
    dom.addEventListener('dragover', onDragOver);
    dom.addEventListener('dragleave', onDragLeave);
    dom.addEventListener('drop', onDrop);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dom as any).__noteDndCleanup = () => {
      dom.removeEventListener('dragstart', onDragStart);
      dom.removeEventListener('dragend', onDragEnd);
      dom.removeEventListener('dragover', onDragOver);
      dom.removeEventListener('dragleave', onDragLeave);
      dom.removeEventListener('drop', onDrop);
    };
  };

  return (
    <Pressable
      ref={onDomRef}
      accessibilityLabel={`Open note ${noteDisplayTitle(note)}`}
      onPress={onOpen}
      style={({ pressed, hovered }) => [
        styles.card,
        webTransition,
        hovered && styles.cardHover,
        pressed && { opacity: 0.85 },
        dragging && styles.cardDragging,
        dropTarget && !dragging && styles.cardDropTarget,
      ]}>
      <View style={styles.cardHead}>
        {/* Drag handle — only shows on web when draggable is enabled.
            The whole card is the drop target, but the handle gives a
            visual cue and an obvious grab affordance. */}
        {draggable && Platform.OS === 'web' ? (
          <View style={styles.dragHandle}>
            <GripVertical size={12} color={Palette.inkFaint} strokeWidth={2} />
          </View>
        ) : null}
        {note.pinned === 1 ? (
          <Pin size={12} color={Palette.honeyDeep} strokeWidth={2.2} />
        ) : null}
        <Text style={styles.cardTitle} numberOfLines={1}>
          {noteDisplayTitle(note)}
        </Text>
      </View>
      {/* If the note body is just a URL, swap the rich-text preview
          for a link-preview card (OG image, title, description, host
          chip). Falls back to RichTextView for normal note content. */}
      <View style={styles.cardBody}>
        {(() => {
          const url = extractSingleUrl(note.body);
          if (url) return <LinkPreviewCard url={url} variant="compact" />;
          return <RichTextView html={note.body} />;
        })()}
      </View>
      <View style={styles.cardFoot}>
        <Text style={styles.cardMeta}>{updatedAgo}</Text>
      </View>
    </Pressable>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const day = 24 * 60 * 60 * 1000;
  if (diff < 60_000) return 'just now';
  if (diff < 60 * 60_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < day) return `${Math.floor(diff / (60 * 60_000))}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Palette.sand },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.three,
    paddingHorizontal: 18,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.two,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  h1: {
    fontFamily: Fonts.serif,
    fontSize: 24,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  sub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  newBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: Spacing.three,
    paddingVertical: 9,
    borderRadius: 999,
    backgroundColor: Palette.forest,
  },
  newBtnText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    letterSpacing: 0.2,
  },
  searchRow: {
    paddingHorizontal: 18,
    paddingBottom: Spacing.two,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 999,
    paddingHorizontal: Spacing.three,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 9,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.ink,
  },
  body: {
    padding: 18,
    paddingBottom: 100,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
  },
  card: {
    flex: 1,
    minWidth: 280,
    maxWidth: 480,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    gap: Spacing.two,
  },
  cardHover: {
    borderColor: Palette.forestLight,
    backgroundColor: '#FBF6EA',
  },
  // Source card while a drag is in flight — fade so the user sees
  // it's "lifted" without losing its position entirely.
  cardDragging: {
    opacity: 0.4,
  },
  // Hover target during a drag — forest dashed border + slight bg
  // lift so the user can tell exactly where the drop will land.
  cardDropTarget: {
    borderColor: Palette.forest,
    borderStyle: 'dashed',
    backgroundColor: '#EAF0EA',
  },
  // GripVertical handle that prefixes the title row when drag-drop is
  // enabled. On web cursor: grab is set via the underlying div's
  // draggable=true attribute, so the handle is purely visual.
  dragHandle: {
    paddingRight: 2,
    opacity: 0.7,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  cardTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 14.5,
    color: Palette.ink,
    letterSpacing: -0.1,
    flexShrink: 1,
  },
  cardBody: {
    maxHeight: 130,
    overflow: 'hidden',
  },
  cardFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: Palette.line,
    paddingTop: Spacing.two,
    marginTop: Spacing.one,
  },
  cardMeta: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkFaint,
  },
  empty: {
    alignItems: 'center',
    paddingVertical: Spacing.eight,
    gap: Spacing.three,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: Palette.honeySoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 20,
    color: Palette.ink,
  },
  emptyText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    lineHeight: 19,
    color: Palette.inkSoft,
    textAlign: 'center',
    maxWidth: 480,
  },
  emptyCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.four,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: Palette.forest,
    marginTop: Spacing.two,
  },
  emptyCtaText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
  noResults: {
    paddingVertical: Spacing.six,
    alignItems: 'center',
  },
  noResultsText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
});
